'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams } from '@/lib/router';
import { TargetIcon, PlusIcon } from '@phosphor-icons/react';
import { useFinance } from '@/hooks/useFinance';
import { useGoalsChanged } from '@/hooks/useGoalsChanged';
import { track } from '@/lib/telemetry';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageSkeleton } from '@/components/PageSkeleton';
import { ErrorState } from '@/components/ErrorState';
import type { GoalsOverview, GoalSummary } from '@/types';
import { PlannerSummary } from '@/components/metas/PlannerSummary';
import { PortfolioTimeline } from '@/components/metas/PortfolioTimeline';
import { AllocationDonut } from '@/components/metas/AllocationDonut';
import { ReservaCard } from '@/components/metas/ReservaCard';
import { ReservaSuggestion } from '@/components/metas/ReservaSuggestion';
import { GoalCard } from '@/components/metas/GoalCard';
import { GoalSimulator } from '@/components/metas/GoalSimulator';

export function MetasView() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { fetchGoalsOverview, loading, error } = useFinance();

  const [overview, setOverview] = useState<GoalsOverview | null>(null);
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(searchParams.get('selected'));

  // The newest load: one that a later load overtook (two goal writes in a row) drops its answer.
  const latestLoadRef = useRef(0);
  const loadData = useCallback(async () => {
    const load = latestLoadRef.current + 1;
    latestLoadRef.current = load;
    try {
      const data = await fetchGoalsOverview();
      if (load === latestLoadRef.current) setOverview(data);
    } catch {
      // handled by hook
    }
  }, [fetchGoalsOverview]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  useGoalsChanged(loadData);

  const selectedGoal = useMemo<GoalSummary | null>(
    () => overview?.goals.find((g) => g.id === selectedGoalId) ?? null,
    [overview, selectedGoalId],
  );

  // The simulator opens from cards, the timeline AND deep links
  // (/metas?selected=<id>, e.g. the control-plane CTA) — track the
  // closed→open transition so every path counts exactly once.
  const simulatorWasOpen = useRef(false);
  useEffect(() => {
    const open = selectedGoal != null;
    if (open && !simulatorWasOpen.current) track('action.goal.open_simulator');
    simulatorWasOpen.current = open;
  }, [selectedGoal]);

  if (error && !overview) {
    return (
      <ErrorState
        title="Erro ao carregar metas"
        message="Não conseguimos carregar suas metas agora. Verifique sua internet e tente novamente."
        onAction={() => {
          void loadData();
        }}
      />
    );
  }

  if (loading && !overview) {
    return <PageSkeleton />;
  }

  const goals = overview?.goals ?? [];
  const hasGoals = goals.length > 0;

  const reserva = goals.find((g) => g.type === 'Reserve');
  const savingsGoals = goals.filter((g) => g.type === 'Savings');
  const debtGoals = goals.filter((g) => g.type === 'DebtPayoff');

  return (
    <div data-tokens="v2" className="space-y-6">
      <PageHeader
        title="Metas"
        titleClassName="font-display text-2xl font-semibold tracking-normal sm:text-3xl"
        actions={
          hasGoals ? (
            <Button
              size="sm"
              onClick={() => router.push('/metas/new')}
            >
              <PlusIcon className="mr-1.5 size-4" />
              Nova meta
            </Button>
          ) : null
        }
      />

      {!hasGoals ? (
        <Card variant="flat" className="rounded-[var(--radius-3xl)] border-2 border-dashed border-border bg-surface shadow-[var(--shadow-1)]">
          <CardContent className="flex flex-col items-center justify-center gap-4 py-16">
            <div className="flex size-16 items-center justify-center rounded-full bg-success-surface">
              <TargetIcon className="size-8 text-success" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-bold text-text-primary">Nenhuma meta criada</h3>
              <p className="mt-1 text-sm text-text-secondary">
                Crie sua primeira meta financeira para acompanhar seu progresso.
              </p>
            </div>
            <Button
              onClick={() => router.push('/metas/new')}
              className="mt-2"
            >
              <PlusIcon className="mr-2 size-4" />
              Criar Meta
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {overview && <PlannerSummary overview={overview} />}

          <section className="grid grid-cols-12 gap-3">
            <div className="col-span-12 min-[880px]:col-span-7">
              <PortfolioTimeline goals={goals} onPickGoal={(g) => setSelectedGoalId(g.id)} />
            </div>
            <div className="col-span-12 min-[880px]:col-span-5">
              <AllocationDonut
                goals={goals}
                totalCommitted={overview?.totalCommitted ?? 0}
                onPickGoal={(g) => setSelectedGoalId(g.id)}
              />
            </div>
          </section>

          <section>
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-sm font-semibold text-text-primary">Reserva de Emergência</h2>
              <span className="text-2xs text-text-secondary">fundação · prioridade máxima</span>
            </div>
            {reserva ? (
              <ReservaCard
                goal={reserva}
                essentialMonthly={overview?.essentialMonthly ?? 0}
                averageMonthlyExpense={overview?.averageMonthlyExpense ?? 0}
                onEdit={() => setSelectedGoalId(reserva.id)}
              />
            ) : (
              <ReservaSuggestion averageMonthlyExpense={overview?.averageMonthlyExpense ?? 0} />
            )}
          </section>

          <section>
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-sm font-semibold text-text-primary">Metas de poupança</h2>
              <span className="rounded bg-surface-sunken px-1.5 py-0.5 font-mono text-2xs text-text-secondary tabular-nums">
                {savingsGoals.length}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-3 min-[880px]:grid-cols-2">
              {savingsGoals.map((g) => (
                <GoalCard key={g.id} goal={g} onEdit={() => setSelectedGoalId(g.id)} />
              ))}
            </div>
          </section>

          {debtGoals.length > 0 && (
            <section>
              <div className="mb-3 flex items-center gap-2">
                <h2 className="text-sm font-semibold text-text-primary">Quitação de dívidas</h2>
                <span className="rounded bg-surface-sunken px-1.5 py-0.5 font-mono text-2xs text-text-secondary tabular-nums">
                  {debtGoals.length}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 min-[880px]:grid-cols-2">
                {debtGoals.map((g) => (
                  <GoalCard key={g.id} goal={g} onEdit={() => setSelectedGoalId(g.id)} />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <GoalSimulator
        goal={selectedGoal}
        onClose={() => setSelectedGoalId(null)}
        onApplied={() => {
          void loadData();
        }}
      />
    </div>
  );
}
